import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { toast } from 'react-toastify';
import settingsAPI from '../../../services/settingsService';
import patientService from '../../../services/patientService';
import { formatPhoneForDisplay } from '../../../utils/phoneUtils';
import './PatientSettings.css';

const PatientSettings = () => {
  const { user } = useSelector((state) => state.auth);
  const { updateBreadcrumbs } = useBreadcrumb();

  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionTime, setSessionTime] = useState(60);
  const [twoWayAuthEnabled, setTwoWayAuthEnabled] = useState(false);
  const [patientData, setPatientData] = useState(null);

  useEffect(() => {
    updateBreadcrumbs([
      { label: 'Dashboard', path: '/patient-dashboard' },
      { label: 'Settings', path: '/patient-settings', active: true }
    ]);
  }, [updateBreadcrumbs]);

  useEffect(() => {
    if (user && user.id) {
      fetchAccountSettings();
    }
  }, [user]);

  const fetchAccountSettings = async () => {
    try {
      setLoading(true);
      
      // Fetch account settings from settings API (works for all roles)
      try {
        const settingsResponse = await settingsAPI.getUserAccountSettings();
        if (settingsResponse.success) {
          setAccountData(settingsResponse.data);
          setSessionTime(settingsResponse.data.session_time || 60);
          setTwoWayAuthEnabled(settingsResponse.data.is_two_way_auth === 1);
        }
      } catch (error) {
        console.log('Settings API not available, using patient data');
      }

      // Also fetch patient-specific data
      let practiceId = user.practice_id || (user.practices && user.practices[0]?.id);
      let patientId = user.patient_id;

      if (!patientId || !practiceId) {
        try {
          const response = await patientService.getCurrentPatient();
          if (response.data?.success && response.data.data) {
            const patient = response.data.data;
            practiceId = patient.practice_id;
            patientId = patient.id;
            setPatientData(patient);
          }
        } catch (apiError) {
          console.error('Error fetching patient data:', apiError);
        }
      } else {
        setPatientData({ practice_id: practiceId, id: patientId });
      }

      if (practiceId && patientId) {
        const detailsResponse = await patientService.getPatientDetails(practiceId, patientId);
        
        if (detailsResponse.data?.success) {
          const fullData = detailsResponse.data.data;
          const patientInfo = fullData.patient || {};
          
          // Merge settings data with patient data
          const fullName = `${patientInfo.first_name || ''} ${patientInfo.last_name || ''}`.trim();
          
          setAccountData(prev => ({
            ...prev,
            full_name: fullName || `${user.first_name || ''} ${user.last_name || ''}`.trim() || '-',
            username: user.username || '-',
            email: patientInfo.email || user.email || prev?.email || '-',
            phone: patientInfo.phone_number || prev?.phone || '-',
            fax: prev?.fax || '-',
            session_time: prev?.session_time || 60,
            is_two_way_auth: prev?.is_two_way_auth !== undefined ? prev.is_two_way_auth : (user.is_two_way_auth || 0)
          }));
          
          if (accountData?.is_two_way_auth === undefined) {
            setTwoWayAuthEnabled(user.is_two_way_auth === 1);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching account settings:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to fetch account settings');
    } finally {
      setLoading(false);
    }
  };


  const handleUpdateSession = async () => {
    try {
      const response = await settingsAPI.updateSessionSettings(sessionTime);
      if (response.success) {
        toast.success(response.message || 'Session time updated successfully');
        fetchAccountSettings();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update session time');
    }
  };

  const toggleTwoWayAuth = async () => {
    const newStatus = twoWayAuthEnabled ? 0 : 1;
    try {
      const response = await settingsAPI.toggleTwoWayAuth(newStatus);
      if (response.success) {
        toast.success(response.message || 'Two-way authentication updated');
        setTwoWayAuthEnabled(newStatus === 1);
        fetchAccountSettings();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to toggle two-way authentication');
    }
  };

  const handleClearCache = async () => {
    try {
      const response = await settingsAPI.clearCache();
      if (response.success) {
        toast.success(response.message || 'Cache cleared successfully');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to clear cache');
    }
  };


  const renderContent = () => {
    if (loading) {
      return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
    }
    if (!accountData) {
      return <div style={{ textAlign: 'center', padding: '40px' }}>No data available</div>;
    }
    return (
      <fieldset className="patient-settings-account-fieldset">
        <legend>Account Settings</legend>
        <div className="patient-settings-account-grid">
          {/* Row 1: Left - Username, Right - Phone */}
          <div className="patient-settings-label">Username</div>
          <div className="patient-settings-sep">:</div>
          <div className="patient-settings-value">{accountData.username || '-'}</div>

          <div className="patient-settings-label">Phone</div>
          <div className="patient-settings-sep">:</div>
          <div className="patient-settings-value">{formatPhoneForDisplay(accountData.phone, '-')}</div>

          {/* Row 2: Left - Fax, Right - Email */}
          <div className="patient-settings-label">Fax</div>
          <div className="patient-settings-sep">:</div>
          <div className="patient-settings-value">{formatPhoneForDisplay(accountData.fax, '-')}</div>

          <div className="patient-settings-label">Email</div>
          <div className="patient-settings-sep">:</div>
          <div className="patient-settings-value">{accountData.email || '-'}</div>

          {/* Row 3: Left - Two Way Authentication, Right - Current Session Time */}
          <div className="patient-settings-label">Two Way Authentication</div>
          <div className="patient-settings-sep">:</div>
          <div className="patient-settings-value">
            <button
              className={`patient-settings-badge ${twoWayAuthEnabled ? 'enabled' : 'disabled'}`}
              onClick={toggleTwoWayAuth}
            >
              {twoWayAuthEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          <div className="patient-settings-label">Current Session Time</div>
          <div className="patient-settings-sep">:</div>
          <div className="patient-settings-value">{accountData.session_time || 60} minutes</div>

          {/* Row 4: Left - Session Time (input + Update), Right - Clear Cache */}
          <div className="patient-settings-label">Session Time (minutes)</div>
          <div className="patient-settings-sep">:</div>
          <div className="patient-settings-value patient-settings-session-control">
            <input
              type="number"
              className="patient-settings-session-input"
              min={1}
              value={sessionTime}
              onChange={(e) => setSessionTime(Number(e.target.value))}
            />
            <button className="patient-settings-update-btn" onClick={handleUpdateSession}>
              Update
            </button>
          </div>

          <div className="patient-settings-label">Clear Cache</div>
          <div className="patient-settings-sep">:</div>
          <div className="patient-settings-value">
            <button className="patient-settings-clear-cache-btn" onClick={handleClearCache}>
              Refresh
            </button>
          </div>
        </div>
      </fieldset>
    );
  };

  return (
    <div className="patient-settings-page-wrapper">
      <h1 className="patient-settings-page-title">Settings</h1>
      <div className="patient-settings-card">
        <div className="patient-settings-tabs" role="tablist">
          <button type="button" className="patient-settings-tab active">
            Account Setting
          </button>
        </div>
        <div className="patient-settings-content">{renderContent()}</div>
      </div>
    </div>
  );
};

export default PatientSettings;
